import React, { Fragment, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import { Bot, ChevronDown, Check, Repeat } from 'lucide-react';
import { clsx } from 'clsx';
import { usePermissions } from '../../contexts/AuthContext';
import WorkspacePill from '../../components/layout/WorkspacePill';
import BusinessFilter from '../../components/layout/BusinessFilter';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { queryLogApi, type QueryLogItem } from '../../services/query-log-api';
import { sourcesApi } from '../../services/sources-api';
import { teamApi } from '../../services/team-api';
import QueryLogDetailDrawer from './QueryLogDetailDrawer';

const WINDOWS = [
  { days: 1, label: 'Last 24 hours' },
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
];

const QueryLogPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { canManageAccounts } = usePermissions();
  const isAdmin = canManageAccounts();

  const [days, setDays] = useState(7);
  // Browsing spans the workspace; this narrows it (§8, page-local).
  const [orgId, setOrgId] = useState<string | null>(null);
  const [sourceId, setSourceId] = useState<string | null>(searchParams.get('source'));
  const [userId, setUserId] = useState<string | null>(null);
  const [failedOnly, setFailedOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const log = useInfiniteQuery({
    queryKey: ['queryLog', { days, orgId, sourceId, userId, failedOnly }],
    queryFn: ({ pageParam }) =>
      queryLogApi.getPage({
        days,
        organizationId: orgId ?? undefined,
        sourceId: sourceId ?? undefined,
        userId: userId ?? undefined,
        failedOnly,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const { data: overview } = useQuery({
    queryKey: ['sourcesOverview'],
    queryFn: () => sourcesApi.getOverview(),
    staleTime: 60_000,
  });
  const { data: orgUsers } = useQuery({
    queryKey: ['orgUsers'],
    queryFn: () => teamApi.listUsers(),
    enabled: isAdmin,
    staleTime: 60_000,
  });

  // The source list follows the business filter, so the two can't disagree.
  const sources = useMemo(
    () =>
      (overview?.organizations ?? [])
        .filter((o) => !orgId || o.id === orgId)
        .flatMap((o) => o.sources.map((s) => ({ id: s.id, name: s.name }))),
    [overview, orgId],
  );
  const items = useMemo(() => (log.data?.pages ?? []).flatMap((p) => p.items), [log.data]);
  const summary = log.data?.pages[0]?.summary;

  const dayGroups = useMemo(() => groupByDay(items), [items]);

  return (
    <div className="max-w-7xl mx-auto px-6 pt-7 pb-24">
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-2xl font-semibold text-foreground">Query Log</h1>
        <WorkspacePill />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mt-5">
        <ChipMenu
          label={WINDOWS.find((w) => w.days === days)?.label ?? 'Window'}
          active
          options={WINDOWS.map((w) => ({ id: String(w.days), label: w.label }))}
          selected={String(days)}
          onSelect={(id) => setDays(Number(id))}
        />
        <BusinessFilter
          value={orgId}
          onChange={(id) => {
            setOrgId(id);
            setSourceId(null);
          }}
          orgs={(overview?.organizations ?? []).map((o) => ({ id: o.id, name: o.name }))}
        />
        <ChipMenu
          label={sourceId ? sources.find((s) => s.id === sourceId)?.name ?? 'Source' : 'All sources'}
          options={[{ id: '', label: 'All sources' }, ...sources.map((s) => ({ id: s.id, label: s.name }))]}
          selected={sourceId ?? ''}
          onSelect={(id) => setSourceId(id || null)}
        />
        {isAdmin && (
          <ChipMenu
            label={
              userId
                ? (orgUsers?.users ?? []).find((u) => u.id === userId)?.firstName ?? 'Person'
                : 'All people'
            }
            options={[
              { id: '', label: 'All people' },
              ...(orgUsers?.users ?? []).map((u) => ({
                id: u.id,
                label: `${u.firstName} ${u.lastName ?? ''}`.trim() || u.email,
              })),
            ]}
            selected={userId ?? ''}
            onSelect={(id) => setUserId(id || null)}
          />
        )}
        <button
          onClick={() => setFailedOnly((v) => !v)}
          className={clsx(
            'inline-flex items-center gap-1.5 rounded-[10px] border px-3 py-1.5 text-[12.5px] font-semibold',
            failedOnly
              ? 'bg-warn/20 border-warn/40 text-warn'
              : 'bg-card border-input text-warn hover:bg-warn/10',
          )}
        >
          Failures only{summary ? ` · ${summary.failed}` : ''}
        </button>
        {summary && (
          <span className="ml-auto text-xs text-muted-foreground">
            {summary.total.toLocaleString()} calls
            {summary.total > 0 && ` · ${((summary.failed / summary.total) * 100).toFixed(1)}% failed`}
            {summary.busiestSource && ` · busiest: ${summary.busiestSource}`}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="mt-3.5 rounded-[10px] border border-border bg-card shadow-sm overflow-x-auto">
        {log.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
            No activity in this window{failedOnly ? ' matching the filter' : ''}.
          </div>
        ) : (
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr>
                {['Time', 'Initiated by', 'Via', 'Request', 'Source', 'Rows', 'Took', 'Status'].map(
                  (h, i) => (
                    <th
                      key={h}
                      className={clsx(
                        'text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-3.5 py-2.5 border-b border-border whitespace-nowrap',
                        i >= 5 && i <= 6 ? 'text-right' : 'text-left',
                      )}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {dayGroups.map((group) => (
                <Fragment key={group.label}>
                  <tr>
                    <td
                      colSpan={8}
                      className="bg-muted/70 px-3.5 py-1.5 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground"
                    >
                      {group.label}
                    </td>
                  </tr>
                  {group.items.map((item) => (
                    <LogRow
                      key={item.id}
                      item={item}
                      selected={selectedId === item.id}
                      onSelect={() => setSelectedId(item.id)}
                    />
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
        {log.hasNextPage && (
          <div className="flex justify-center border-t border-border py-3">
            <button
              onClick={() => void log.fetchNextPage()}
              disabled={log.isFetchingNextPage}
              className="rounded-[10px] border border-input bg-card px-4 py-1.5 text-[13px] font-medium text-foreground hover:bg-accent disabled:opacity-50"
            >
              {log.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>

      <QueryLogDetailDrawer id={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
};

// ── Row ─────────────────────────────────────────────────────────────────

const LogRow: React.FC<{
  item: QueryLogItem;
  selected: boolean;
  onSelect: () => void;
}> = ({ item, selected, onSelect }) => {
  const time = new Date(item.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const isSystem = item.initiatedBy.kind !== 'human';

  return (
    <tr
      className={clsx('cursor-pointer hover:bg-muted/50', selected && 'bg-muted/60')}
      onClick={onSelect}
    >
        <td className="px-3.5 py-2.5 border-b border-border whitespace-nowrap align-top text-muted-foreground">
          {time}
        </td>
        <td className="px-3.5 py-2.5 border-b border-border whitespace-nowrap align-top">
          <span className="flex items-center gap-2">
            <span
              className={clsx(
                'w-6 h-6 flex items-center justify-center text-[9px] font-extrabold flex-none',
                isSystem
                  ? 'rounded-[7px] bg-muted text-muted-foreground border border-border'
                  : 'rounded-full bg-primary text-primary-foreground',
              )}
            >
              {isSystem ? (
                item.initiatedBy.kind === 'routine' ? (
                  <Repeat className="w-3 h-3" />
                ) : (
                  <Bot className="w-3 h-3" />
                )
              ) : (
                initials(item.initiatedBy.name)
              )}
            </span>
            <span className="min-w-0">
              <span className="font-semibold text-foreground">{item.initiatedBy.name}</span>
              {item.initiatedBy.sub && (
                <div className="text-[10.5px] text-muted-foreground">{item.initiatedBy.sub}</div>
              )}
            </span>
          </span>
        </td>
        <td className="px-3.5 py-2.5 border-b border-border whitespace-nowrap align-top">
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2 py-0.5',
              item.via.kind === 'chat' && 'bg-primary/15 text-primary',
              item.via.kind === 'mcp' && 'bg-info/15 text-info',
              (item.via.kind === 'automation' || item.via.kind === 'admin') &&
                'bg-muted text-muted-foreground',
            )}
          >
            {item.via.label}
          </span>
        </td>
        <td className="px-3.5 py-2.5 border-b border-border align-top max-w-[330px]">
          <div className="font-semibold text-foreground whitespace-normal">
            {requestSummary(item)}
          </div>
          <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{item.toolName}</div>
          {item.errorMessage && (
            <div className="text-[11px] text-destructive mt-0.5 whitespace-normal">
              {item.denied ? 'Denied — ' : ''}
              {item.errorMessage}
            </div>
          )}
        </td>
        <td className="px-3.5 py-2.5 border-b border-border whitespace-nowrap align-top text-foreground">
          {item.sourceName ?? '—'}
        </td>
        <td className="px-3.5 py-2.5 border-b border-border whitespace-nowrap align-top text-right tabular-nums text-foreground">
          {item.rowCount ?? '—'}
        </td>
        <td className="px-3.5 py-2.5 border-b border-border whitespace-nowrap align-top text-right tabular-nums text-muted-foreground">
          {formatMs(item.tookMs)}
        </td>
        <td className="px-3.5 py-2.5 border-b border-border whitespace-nowrap align-top">
          <span
            className={clsx(
              'inline-flex text-[10.5px] font-semibold rounded-full px-2 py-0.5',
              item.status === 'success'
                ? 'bg-success/15 text-success'
                : 'bg-destructive/15 text-destructive',
            )}
          >
            {item.status === 'success' ? 'OK' : item.denied ? 'Denied' : 'Failed'}
          </span>
        </td>
    </tr>
  );
};

const ChipMenu: React.FC<{
  label: string;
  active?: boolean;
  options: Array<{ id: string; label: string }>;
  selected: string;
  onSelect: (id: string) => void;
}> = ({ label, active, options, selected, onSelect }) => (
  <Menu as="div" className="relative">
    <Menu.Button
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-[10px] border px-3 py-1.5 text-[12.5px]',
        active
          ? 'bg-accent border-primary/40 text-primary font-semibold'
          : 'bg-card border-input text-foreground hover:bg-accent',
      )}
    >
      {label} <ChevronDown className="w-3.5 h-3.5" />
    </Menu.Button>
    <Menu.Items className="absolute left-0 top-9 z-20 min-w-[190px] max-h-72 overflow-y-auto bg-card border border-border rounded-[10px] shadow-lg p-1 focus:outline-none">
      {options.map((o) => (
        <Menu.Item key={o.id}>
          {({ active: hovered }) => (
            <button
              onClick={() => onSelect(o.id)}
              className={clsx(
                'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-[13px] text-left',
                hovered && 'bg-muted',
                o.id === selected ? 'text-primary font-medium' : 'text-foreground',
              )}
            >
              <span className="flex-1 truncate">{o.label}</span>
              {o.id === selected && <Check className="w-3.5 h-3.5" />}
            </button>
          )}
        </Menu.Item>
      ))}
    </Menu.Items>
  </Menu>
);

// ── Helpers ─────────────────────────────────────────────────────────────

function groupByDay(items: QueryLogItem[]): Array<{ label: string; items: QueryLogItem[] }> {
  const groups: Array<{ label: string; items: QueryLogItem[] }> = [];
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86_400_000).toDateString();
  for (const item of items) {
    const d = new Date(item.at);
    const ds = d.toDateString();
    const dateLabel = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    const label =
      ds === today ? `Today · ${dateLabel}` : ds === yesterday ? `Yesterday · ${dateLabel}` : dateLabel;
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  }
  return groups;
}

const TOOL_SUMMARIES: Record<string, string> = {
  list_data_source_accounts: 'Listed available data sources',
  get_account_schema: 'Schema lookup before querying',
  execute_read_query: 'Data query',
  db_execute_read_query: 'Database query (live)',
  db_sample_rows: 'Sampled rows from your database',
  search_documents: 'Searched documents',
  get_document_content: 'Read a document',
  get_wiki_index: 'Browsed the knowledge wiki',
  read_wiki_page: 'Read a wiki page',
  edit_wiki_page: 'Updated a wiki page',
  generate_artifact: 'Generated a report artifact',
  execute_python: 'Ran a Python analysis',
  viewer_read_records: 'Browsed records in the Data viewer',
  viewer_read_details: 'Browsed line items in the Data viewer',
  viewer_read_options: 'Browsed item options in the Data viewer',
};

function requestSummary(item: QueryLogItem): string {
  if (TOOL_SUMMARIES[item.toolName]) return TOOL_SUMMARIES[item.toolName];
  if (item.toolName.startsWith('qb_')) {
    return `QuickBooks: ${item.toolName.replace(/^qb_(get_)?/, '').replace(/_/g, ' ')}`;
  }
  if (item.toolName.startsWith('db_')) {
    return `Database: ${item.toolName.replace(/^db_/, '').replace(/_/g, ' ')}`;
  }
  return item.toolName.replace(/_/g, ' ');
}

function formatMs(ms: number | null): string {
  if (ms == null) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}

export default QueryLogPage;
